import { BasketItem } from '../types/basket';
import { getAddressForSubmission } from './address';
import callApi, { ApiCallOptions } from './callApi';
import { AddressSuggestion, CheckoutState, DeliveryOption, GiftCard, Notification } from './checkoutState';
import { config } from './config';
import { isEmpty } from './getValidator';
import {
  getGaEcommerceObject,
  getPriceBi,
  isItemQuantityEqual,
  pushCustomerDataTrackingEvent,
  pushTrackingEvent
} from './tracking';

type SyntheticNotifications = Partial<Record<number | 'success', Pick<Notification, 'type' | 'textTranslationKey'>>>;

type SignInPayload = {
  email: string;
  password: string;
};

type RegisterPayload = {
  gender: string;
  firstName: string;
  lastName: string;
  email: string;
  password?: string;
  hasSubscribedNewsletter?: boolean;
  source: string;
  birthDate?: string;
  phone?: string;
};

type SetPasswordForCustomerNumberPayload = {
  publicKey: string;
  birthDate: string;
  email: string;
  password: string;
};

type SetBasketItemQuantityPayload = {
  id: string;
  quantity: number;
};

type SetBasketItemQuantityContext = {
  prevQuantity: number;
  removedBasketItem?: BasketItem;
};

type RemoveBasketItemPayload = {
  id: string;
};

type RemoveBasketItemContext = {
  prevQuantity: number;
  removedBasketItem: BasketItem;
};

type SetCarrierAndShippingOptionPayload = {
  prevQuantity: number;
  removedBasketItem: BasketItem;
};
type SetCollectionPointPayload = {
  specification: {
    collectionPointId: string;
    collectionPointCustomerNumber?: string;
    customer: { firstName: string; lastName: string };
  };
  data: Pick<
    DeliveryOption,
    'carrierKey' | 'carrierGroupKey' | 'shippingOptionKey' | 'collectionPointType' | 'deliveryOptionKey'
  >;
};

type SetDynamicFieldsPayload = {
  customer?: { birthDate?: string; phone?: string; carrierPhone?: string; hasSubscribedNewsletter?: boolean };
  order?: { taxId?: string; isCarrierConsentAccepted?: boolean };
};

type SetPaymentOptionPayload = { paymentOptionKey: string; recurringPaymentOptionId?: string };

type UpdatePaymentOptionPayload = {
  paymentOptionKey: string;
  currentUrl?: string;
  specification?: any;
};

type RemoveGiftCardPayload = { id: string };

type AddGiftCardPayload = { giftCardCode: string; giftCardPin: string };

type AddDiscountPayload = { code: string };

type RemoveDiscountPayload = { identifier: string };

type RemoveRecurringPaymentMethodPayload = { id: string };

type LoyaltyProgramPayload = { membershipAccountNumber: string; membershipTypeKey: string };

type RedeemLoyaltyProgramPointsPayload = { points: number };

type ConfirmOrderPayload = {
  paymentOptionKey: string;
  recurringPaymentOptionId?: string;
  currentUrl: string;
  specification?: {
    key?: string; // needed for adyen_ideal
    iban?: string; // needed for ratepay_directdebit
    paymentMethod?: Record<string, unknown>; // needed for adyen_creditcard, adyen_paypal, spay_paypal, adyen_klarna_*, adyen_twint
    browserInfo?: Record<string, unknown>; // needed for adyen_creditcard
    origin?: string; // needed for adyen_creditcard and spay_adyen_credit_card
    storePaymentMethod?: boolean;
    paymentTypeId?: string;
    authorizationToken?: string; // needed for spay klarna
  };
};

type ConfirmOrderResponse = { hasRedirect: boolean; hasNotification: boolean };

export type Mutations = {
  signIn: (payload: SignInPayload) => Promise<CheckoutState>;
  register: (payload: RegisterPayload) => Promise<CheckoutState>;
  setPasswordForCustomerNumber: (payload: SetPasswordForCustomerNumberPayload) => Promise<CheckoutState>;
  setBasketItemQuantity: (
    payload: SetBasketItemQuantityPayload,
    context: SetBasketItemQuantityContext
  ) => Promise<number | undefined>;
  removeBasketItem: (payload: RemoveBasketItemPayload, context: RemoveBasketItemContext) => Promise<CheckoutState>;
  setCarrierAndShippingOption: (payload: SetCarrierAndShippingOptionPayload) => Promise<CheckoutState>;
  setCollectionPoint: (payload: SetCollectionPointPayload) => Promise<CheckoutState>;
  setShippingAddress: (payload: ReturnType<typeof getAddressForSubmission>) => Promise<AddressSuggestion[] | undefined>;
  setShippingAddressAsBillingAddress: () => Promise<CheckoutState>;
  setBillingAddress: (payload: ReturnType<typeof getAddressForSubmission>) => Promise<AddressSuggestion[] | undefined>;
  setDynamicFields: (payload: SetDynamicFieldsPayload) => Promise<CheckoutState>;
  forceOrderUpdate: () => Promise<CheckoutState>;
  getState: () => Promise<CheckoutState>;
  setPaymentOption: (payload: SetPaymentOptionPayload) => Promise<CheckoutState>;
  updatePaymentOption: (payload: UpdatePaymentOptionPayload) => Promise<any>;
  addGiftCard: (payload: AddGiftCardPayload) => Promise<GiftCard[] | undefined>;
  removePromotions: () => Promise<CheckoutState>;
  removeGiftCard: (payload: RemoveGiftCardPayload) => Promise<CheckoutState>;
  addVoucher: (payload: AddDiscountPayload) => Promise<CheckoutState>;
  removeVoucher: (payload: RemoveDiscountPayload) => Promise<CheckoutState>;
  addPromotion: (payload: AddDiscountPayload) => Promise<CheckoutState>;
  removePromotion: (payload: RemoveDiscountPayload) => Promise<CheckoutState>;
  removeRecurringPaymentMethod: (payload: RemoveRecurringPaymentMethodPayload) => Promise<CheckoutState>;
  registerForLoyaltyProgram: () => Promise<CheckoutState>;
  setLoyaltyProgram: (payload: LoyaltyProgramPayload) => Promise<CheckoutState>;
  removeLoyaltyProgram: (payload: LoyaltyProgramPayload) => Promise<CheckoutState>;
  redeemLoyaltyProgramPoints: (payload: RedeemLoyaltyProgramPointsPayload) => Promise<CheckoutState>;
  unredeemLoyaltyProgramPoints: () => Promise<CheckoutState>;
  confirmOrder: (payload: ConfirmOrderPayload) => Promise<ConfirmOrderResponse>;
};

// Emulate notifications on legacy endpoints as well as missing notifications on V3 API
const addSyntheticNotification = (
  checkoutState: CheckoutState,
  syntheticNotifications: SyntheticNotifications | undefined,
  errorOrSuccess: any | 'success'
) => {
  const key = errorOrSuccess === 'success' ? 'success' : errorOrSuccess.statusCode;
  const syntheticNotification = syntheticNotifications?.[key];
  if (errorOrSuccess !== 'success' && !syntheticNotification) {
    throw errorOrSuccess;
  } else if (!syntheticNotification) {
    return checkoutState;
  } else {
    return { ...checkoutState!, notifications: [syntheticNotification] };
  }
};

const getMutations = (
  checkoutState: CheckoutState | undefined,
  setCheckoutState: (newState: CheckoutState) => void,
  resetPoll: () => void,
  jwt?: string
): Mutations => {
  const runMutation = async (options: ApiCallOptions & { syntheticNotifications?: SyntheticNotifications }) => {
    let newState: CheckoutState | undefined = undefined;
    // For certain mutations the new V3 endpoints don't exist yet, therefore we have to use the "legacy" endpoints
    // to trigger the operation and get the updated state in a second call
    const securityHash = checkoutState?.customer?.securityHash ?? undefined;
    if (options.flow === 'legacy') {
      try {
        await callApi({ ...options, securityHash });
      } catch (e: any) {
        newState = addSyntheticNotification(checkoutState!, options.syntheticNotifications, e);
      }
      if (!newState) {
        newState = await callApi<CheckoutState>({
          endpoint: '/state',
          failedStatusCodes: [204],
          sequential: true,
          securityHash
        });
        newState = addSyntheticNotification(newState, options.syntheticNotifications, 'success');
      }
    } else {
      try {
        newState = await callApi<CheckoutState>({ ...options, securityHash });
        newState = addSyntheticNotification(newState, options.syntheticNotifications, 'success');
      } catch (e: any) {
        newState = addSyntheticNotification(checkoutState!, options.syntheticNotifications, e);
      }
    }
    setCheckoutState(newState);
    if (!options.stopReset) {
      resetPoll();
    }
    return newState;
  };

  return {
    signIn: async (payload: SignInPayload) => {
      const newState = await runMutation({
        endpoint: '/customer/login',
        payload,
        flow: 'legacy',
        sequential: true,
        syntheticNotifications: {
          404: { type: 'error', textTranslationKey: 'auth.notifications.notFound' },
          405: { type: 'error', textTranslationKey: 'auth.notifications.deactivated' },
          422: { type: 'error', textTranslationKey: 'auth.notifications.notFound' }
        }
      });
      if (newState.customer) {
        pushTrackingEvent({
          event: 'login',
          content_name: document.location.pathname,
          page_type: 'checkout:auth',
          page_type_id: '',
          method: 'email',
          status: 'successful',
          customer_id: newState.customer?.data.id,
          unique_customer_id: newState.customer?.data?.customData?.customer_unique_id,
          eh: newState.customer?.data.trackingHash,
          preselected_payment_method: newState.orderState.payment?.selected?.[0].paymentOptionKey,
          ...(config.tracking.sendPlaintextEmail && { email: payload.email })
        });
        pushCustomerDataTrackingEvent(newState);
      }
      return newState;
    },
    register: async (payload: RegisterPayload) => {
      const newState = await runMutation({
        endpoint: '/customer',
        payload,
        flow: 'legacy',
        sequential: true,
        syntheticNotifications: {
          409: { type: 'error', textTranslationKey: 'auth.notifications.alreadyExists' }
        }
      });
      if (newState.integrations.newsletter.customerIsConfirmedSubscriber) {
        pushTrackingEvent({
          event: 'feature',
          action: 'submit',
          name: 'newsletter',
          label: 'newsletter'
        });
      }
      if (newState.customer) {
        pushTrackingEvent({
          event: 'sign_up',
          content_name: document.location.pathname,
          page_type: 'checkout:auth',
          page_type_id: '',
          method: 'email',
          status: 'successful',
          customer_id: newState.customer?.data.id,
          unique_customer_id: newState.customer?.data?.customData?.customer_unique_id,
          eh: newState.customer?.data.trackingHash,
          preselected_payment_method: newState.orderState.payment?.selected?.[0].paymentOptionKey,
          ...(config.tracking.sendPlaintextEmail && { email: payload.email })
        });
        pushCustomerDataTrackingEvent(newState);
      }
      return newState;
    },
    setPasswordForCustomerNumber: async (payload: SetPasswordForCustomerNumberPayload) => {
      const newState = await runMutation({
        endpoint: '/customer/authenticate',
        payload,
        flow: 'legacy',
        sequential: true,
        syntheticNotifications: {
          success: {
            type: 'success',
            textTranslationKey: 'auth.options.signIn.notifications.signedInWithCustomerNumber'
          },
          409: { type: 'error', textTranslationKey: 'auth.options.signIn.notifications.emailDifferentAccount' },
          412: { type: 'error', textTranslationKey: 'auth.options.signIn.notifications.emailMismatch' },
          423: { type: 'error', textTranslationKey: 'auth.options.signIn.notifications.emailPreviouslySet' }
        }
      });
      if (newState.customer) {
        pushTrackingEvent({
          event: 'set_password',
          content_name: document.location.pathname,
          page_type: 'checkout:auth',
          page_type_id: '',
          method: 'email',
          status: 'successful',
          customer_id: newState.customer?.data.id,
          unique_customer_id: newState.customer?.data?.customData?.customer_unique_id,
          eh: newState.customer?.data.trackingHash,
          ...(config.tracking.sendPlaintextEmail && { email: payload.email })
        });
        pushCustomerDataTrackingEvent(newState);
      }
      return newState;
    },
    setBasketItemQuantity: async (
      { id, quantity }: SetBasketItemQuantityPayload,
      { prevQuantity, removedBasketItem }: SetBasketItemQuantityContext
    ) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: `/state/basket/items/${id}`,
        payload: { quantity },
        allowedStatusCodes: [404, 406],
        failedStatusCodes: [204],
        sequential: true
      });
      if (!isItemQuantityEqual(checkoutState!, newState, id)) {
        const delta = Math.abs(quantity - prevQuantity);
        pushTrackingEvent({
          event: quantity > prevQuantity ? 'add_to_cart' : 'remove_from_cart',
          action: `${window.location.hash}|${id}`,
          price_bi: getPriceBi(newState.orderState.basket, id),
          ecommerce: getGaEcommerceObject(newState, id, delta, removedBasketItem)
        });
      }
      return newState.orderState.basket.packages.flatMap(({ items }) => items).find(i => i.id === id)?.quantity;
    },
    removeBasketItem: async (
      { id }: RemoveBasketItemPayload,
      { prevQuantity, removedBasketItem }: RemoveBasketItemContext
    ) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: `/state/basket/items/${id}`,
        payload: { quantity: 0 },
        allowedStatusCodes: [404, 406],
        failedStatusCodes: [204],
        sequential: true
      });
      if (!isItemQuantityEqual(checkoutState!, newState, id)) {
        const delta = Math.abs(0 - prevQuantity);
        pushTrackingEvent({
          event: 'remove_from_cart',
          action: `${window.location.hash}|${id}`,
          price_bi: getPriceBi(newState.orderState.basket, id),
          ecommerce: getGaEcommerceObject(newState, id, delta, removedBasketItem)
        });
      }
      return newState;
    },
    setCarrierAndShippingOption: async (payload: SetCarrierAndShippingOptionPayload) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/delivery/option',
        payload,
        sequential: true
      });
      const selectedCarrier = newState.orderState.shipping?.selected?.[0];
      pushTrackingEvent({
        event: 'feature',
        action: 'click',
        name: 'add_shipping_info',
        label: `home_delivery|${selectedCarrier?.carrierKey}`,
        shipping_type: 'carrier',
        shipping_method: `${selectedCarrier?.carrierKey}|${selectedCarrier?.shippingOptionKey}`,
        ecommerce: getGaEcommerceObject(newState)
      });
      return newState;
    },
    setCollectionPoint: async ({ specification, data }: SetCollectionPointPayload) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/delivery/option',
        sequential: true,
        payload: { data, specification },
        syntheticNotifications: {
          451: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirement' },
          452: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementIllegalUser' },
          453: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementItemsDenied' }
        }
      });
      const selectedShipping = newState.orderState?.shipping?.selected?.[0];
      pushTrackingEvent({
        event: 'feature',
        action: 'submit',
        name: 'add_shipping_info',
        label: `${selectedShipping?.collectionPointType}|${selectedShipping?.carrierGroupKey}`,
        shipping_type: 'collectionPoint',
        shipping_method: newState.orderState.shipping?.selected?.[0].collectionPointType,
        ecommerce: getGaEcommerceObject(newState)
      });
      return newState;
    },
    setShippingAddress: async (payload: ReturnType<typeof getAddressForSubmission>) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/addresses/shipping',
        payload,
        sequential: true,
        allowedStatusCodes: [460],
        syntheticNotifications: {
          451: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirement' },
          452: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementIllegalUser' },
          453: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementItemsDenied' }
        }
      });
      const selectedCarrier = newState.orderState.shipping?.selected?.[0];

      pushTrackingEvent({
        event: 'feature',
        action: 'submit',
        name: 'add_shipping_info',
        label: `home_delivery|${selectedCarrier?.carrierKey}`,
        shipping_type: 'carrier',
        shipping_method: `${selectedCarrier?.carrierKey}|${selectedCarrier?.shippingOptionKey}`,
        ecommerce: getGaEcommerceObject(newState)
      });
      return newState.addressSuggestions?.suggestions;
    },
    setShippingAddressAsBillingAddress: async () => {
      return await runMutation({
        endpoint: '/state/order/addresses/copy/shipping/to/billing',
        payload: {},
        sequential: true,
        syntheticNotifications: {
          451: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirement' },
          452: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementIllegalUser' },
          453: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementItemsDenied' }
        }
      });
    },
    setBillingAddress: async (payload: ReturnType<typeof getAddressForSubmission>) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/addresses/billing',
        payload,
        sequential: true,
        allowedStatusCodes: [460],
        syntheticNotifications: {
          451: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirement' },
          452: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementIllegalUser' },
          453: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementItemsDenied' }
        }
      });
      if (newState.orderState?.addresses?.billing) {
        pushTrackingEvent({
          event: 'feature',
          action: 'submit',
          name: 'billing_adress',
          label: 'billing_adress'
        });
      }
      return newState.addressSuggestions?.suggestions;
    },
    setDynamicFields: async (payload: SetDynamicFieldsPayload) => {
      const newState = await runMutation({ method: 'PATCH', endpoint: '/state', payload, sequential: true });
      if (payload.customer?.hasSubscribedNewsletter) {
        pushTrackingEvent({
          event: 'feature',
          action: 'submit',
          name: 'newsletter',
          label: 'newsletter'
        });
      }
      return newState;
    },
    forceOrderUpdate: async () => {
      return await runMutation({
        method: 'GET',
        endpoint: '/state',
        urlParams: {
          forceSignatureMismatch: 'true'
        },
        sequential: true
      });
    },
    getState: async () => {
      return await runMutation({
        method: 'GET',
        endpoint: '/state',
        sequential: true
      });
    },
    setPaymentOption: async (payload: { paymentOptionKey: string; recurringPaymentOptionId?: string }) => {
      const newState = await runMutation({
        endpoint: '/state/order/payment/option',
        payload: { data: payload },
        allowedStatusCodes: [405, 406],
        sequential: true
      });
      pushTrackingEvent({
        event: 'feature',
        action: 'click',
        name: 'payment_method',
        label: `payment_method|${newState.orderState.payment?.selected?.[0].paymentOptionKey}`
      });
      pushTrackingEvent({
        event: 'add_payment_info',
        ecommerce: getGaEcommerceObject(newState),
        payment_type: newState.orderState.payment?.selected?.[0].paymentOptionKey
      });
      return newState;
    },
    updatePaymentOption: async ({ paymentOptionKey, currentUrl, specification }: UpdatePaymentOptionPayload) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/order/payment/option',
        payload: { data: { paymentOptionKey }, specification, referer: currentUrl },
        allowedStatusCodes: [405, 406],
        sequential: true
      });
      return newState.orderState.payment?.selected?.[0].configuration?.initializationData;
    },
    addGiftCard: async (payload: { giftCardCode: string; giftCardPin: string }) => {
      const newState = await runMutation({
        endpoint: '/payment/customer/giftcard',
        payload: { asPaymentMethod: true, ...payload },
        flow: 'legacy',
        syntheticNotifications: {
          success: { type: 'success', textTranslationKey: 'payment.giftCard.notifications.success' },
          404: { type: 'error', textTranslationKey: 'payment.giftCard.notifications.notFound' },
          406: { type: 'error', textTranslationKey: 'payment.giftCard.notifications.noBalance' },
          409: { type: 'error', textTranslationKey: 'payment.giftCard.notifications.alreadyActive' }
        }
      });
      if (newState.orderState.payment?.giftCards?.length) {
        pushTrackingEvent({
          event: 'feature',
          name: 'gift_card',
          action: 'submit',
          label: null
        });
      }
      return newState.orderState.payment?.giftCards;
    },
    removePromotions: async () => {
      return await runMutation({
        method: 'DELETE',
        endpoint: '/state/basket/promotions'
      });
    },
    removeGiftCard: async ({ id }: RemoveGiftCardPayload) => {
      const newState = await runMutation({
        method: 'DELETE',
        endpoint: `/state/order/payment/recurring/${id}`,
        syntheticNotifications: {
          success: { type: 'success', textTranslationKey: 'payment.giftCard.notifications.removalSuccess' }
        }
      });
      pushTrackingEvent({
        event: 'feature',
        name: 'gift_card',
        action: 'remove',
        label: null
      });
      return newState;
    },
    addVoucher: async (payload: AddDiscountPayload) => {
      const newState = await runMutation({
        endpoint: '/state/voucher',
        payload,
        sequential: true,
        allowedStatusCodes: [409],
        jwt: !isEmpty(jwt) ? jwt : undefined
      });
      if (newState.orderState.vouchers?.length) {
        pushTrackingEvent({ event: 'feature', name: 'voucher', action: 'submit', label: payload.code });
      }
      return newState;
    },
    removeVoucher: async ({ identifier }: RemoveDiscountPayload) => {
      const newState = await runMutation({
        method: 'DELETE',
        endpoint: `/voucher/${identifier}`,
        flow: 'legacy',
        sequential: true,
        syntheticNotifications: {
          success: { type: 'success', textTranslationKey: 'payment.voucher.notifications.removalSuccess' }
        }
      });
      pushTrackingEvent({ event: 'feature', name: 'voucher', action: 'remove', label: identifier });
      return newState;
    },
    addPromotion: async (payload: AddDiscountPayload) => {
      const newState = await runMutation({
        endpoint: '/state/promotion-code',
        payload,
        sequential: true,
        allowedStatusCodes: [409]
      });
      if (newState.orderState.promotions?.length) {
        pushTrackingEvent({ event: 'feature', action: 'submit', label: payload.code, value: null });
      }
      return newState;
    },
    removePromotion: async ({ identifier }: RemoveDiscountPayload) => {
      const newState = await runMutation({
        method: 'DELETE',
        endpoint: `/state/promotion-code/${identifier}`,
        sequential: true,
        syntheticNotifications: {
          success: { type: 'success', textTranslationKey: 'payment.promotion.notifications.removalSuccess' }
        }
      });
      pushTrackingEvent({ event: 'feature', action: 'remove', label: null, value: null });
      return newState;
    },
    removeRecurringPaymentMethod: async ({ id }: RemoveRecurringPaymentMethodPayload) => {
      return await runMutation({
        method: 'DELETE',
        endpoint: `/payment/recurring/${id}`,
        flow: 'legacy',
        sequential: true
      });
    },
    registerForLoyaltyProgram: async () => {
      const newState = await runMutation({
        endpoint: '/state/memberships/register',
        payload: {},
        sequential: true,
        allowedStatusCodes: [401, 404, 409]
      });
      if (newState.orderState.memberships?.activated?.length) {
        pushTrackingEvent({
          event: 'loyalty_program',
          action: 'register',
          label: null,
          value: newState.orderState.memberships?.activated[0].membershipAccountNumber
        });
      }
      return newState;
    },
    setLoyaltyProgram: async (payload: LoyaltyProgramPayload) => {
      const newState = await runMutation({
        endpoint: '/state/memberships',
        payload,
        sequential: true,
        allowedStatusCodes: [401, 404, 409, 422]
      });
      if (newState.orderState.memberships?.activated?.length) {
        pushTrackingEvent({
          event: 'loyalty_program',
          action: 'add',
          label: null,
          value: newState.orderState.memberships?.activated[0].membershipAccountNumber
        });
      }
      return newState;
    },
    removeLoyaltyProgram: async ({ membershipAccountNumber, membershipTypeKey }: LoyaltyProgramPayload) => {
      const newState = await runMutation({
        method: 'DELETE',
        endpoint: `/state/memberships/${membershipTypeKey}/${membershipAccountNumber}`,
        sequential: true,
        allowedStatusCodes: [401, 404]
      });
      pushTrackingEvent({ event: 'loyalty_program', action: 'remove', label: null, value: membershipAccountNumber });
      return newState;
    },
    redeemLoyaltyProgramPoints: async (payload: { points: number }) => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/memberships',
        payload,
        sequential: true,
        allowedStatusCodes: [401, 404]
      });
      pushTrackingEvent({
        event: 'loyalty_program',
        action: 'redeem',
        label: 'redeemed points',
        value: payload.points
      });
      return newState;
    },
    unredeemLoyaltyProgramPoints: async () => {
      const newState = await runMutation({
        method: 'PUT',
        endpoint: '/state/memberships',
        payload: { points: 0 },
        sequential: true,
        allowedStatusCodes: [401, 404]
      });
      pushTrackingEvent({
        event: 'loyalty_program',
        action: 'unredeem',
        label: 'unredeem points',
        value: 0
      });
      return newState;
    },
    confirmOrder: async ({
      paymentOptionKey,
      recurringPaymentOptionId,
      currentUrl,
      specification
    }: ConfirmOrderPayload) => {
      const newState = await runMutation({
        endpoint: '/state/order/confirmation/execute',
        payload: {
          data: { paymentOptionKey, recurringPaymentOptionId },
          specification,
          referer: currentUrl
        },
        allowedStatusCodes: [402, 406, 412],
        sequential: true,
        stopReset: true,
        syntheticNotifications: {
          451: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirement' },
          452: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementIllegalUser' },
          453: { type: 'error', textTranslationKey: 'payment.notifications.legalRequirementItemsDenied' }
        }
      });

      const checkoutRedirect = !!newState.orderConfirmation?.redirect;
      const adyenSDKRedirect =
        !!newState?.orderState?.payment?.selected?.[0]?.configuration?.initializationData?.action;

      const hasRedirect = checkoutRedirect || adyenSDKRedirect;
      if (hasRedirect) {
        pushTrackingEvent({
          event: 'complete_checkout',
          action: `${paymentOptionKey}|${newState.orderState.id}|${newState.customer?.data.id}`,
          label: null
        });
      }
      return { hasRedirect, hasNotification: !!newState.notifications };
    }
  };
};

export default getMutations;
